import React, { Fragment, memo, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { ConfirmActionModal } from 'src/components/shared/ConfirmActionModal'
import { Button } from 'src/components/shared/Button'
import { DataRow } from 'src/components/shared/DataRow'
import { SectionCard } from 'src/components/shared/SectionCard'
import { AdminSwitchPartnerDriverOnboarding } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { PartnerDriverDetails } from 'src/interfaces/partnerDriver'
import { getPartnerDriverSaving } from 'src/selectors/partnerDriver'
import { formatDateOnly, parseIsoDate } from 'src/utils/grid'
import { formatDriverName } from 'src/utils/partnerDriver/driverName'

const LOCKED_NOTE =
  "This driver has been activated or deactivated, so onboarding status can't change."

interface Props {
  driver: PartnerDriverDetails
  onChanged(): void
}

const OnboardingStatusCardComponent = ({ driver, onChanged }: Props) => {
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const isSaving = useSelector(getPartnerDriverSaving)
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false)
  const canSwitchOnboarding = useHasPermission(InternalPermission.ContractorOnboardingStatusManage)

  const isOnboarded = Boolean(driver.onboardedAt)
  // The backend answers 400 once the driver has been activated or deactivated.
  const isLocked = Boolean(driver.activatedAt) || Boolean(driver.deactivatedAt)
  const driverName = formatDriverName(driver.firstName, driver.lastName) || 'this driver'

  const handleOpenConfirm = useCallback(() => {
    setIsConfirmOpen(true)
  }, [])

  const handleCloseConfirm = useCallback(() => {
    setIsConfirmOpen(false)
  }, [])

  const handleConfirm = useCallback(async () => {
    const result = await asyncDispatchWithErrorToast<boolean>(
      AdminSwitchPartnerDriverOnboarding.request({
        partnerDriverId: driver.id,
        onboarded: !isOnboarded,
      }),
    )

    if (!result) {
      return
    }

    snackbar?.enqueueSnackbar(isOnboarded ? 'Marked not onboarded' : 'Marked onboarded', {
      variant: 'success',
    })
    setIsConfirmOpen(false)
    onChanged()
  }, [asyncDispatchWithErrorToast, driver.id, isOnboarded, snackbar, onChanged])

  return (
    <Fragment>
      <SectionCard
        title="Onboarding status"
        dataTestId="partner-driver-onboarding-card"
        actions={
          canSwitchOnboarding ? (
            <Button
              size="small"
              color={isOnboarded ? 'white' : 'dark'}
              disabled={isLocked || isSaving}
              onClick={handleOpenConfirm}
              data-testid="partner-driver-onboarding-toggle-button"
            >
              {isOnboarded ? 'Mark not onboarded' : 'Mark onboarded'}
            </Button>
          ) : undefined
        }
      >
        <div>
          <DataRow title="Onboarded" text={isOnboarded ? 'Yes' : 'No'} />
          <DataRow title="Onboarded at" text={formatDateOnly(parseIsoDate(driver.onboardedAt))} />
          <DataRow title="Activated at" text={formatDateOnly(parseIsoDate(driver.activatedAt))} />
          <DataRow
            title="Deactivated at"
            text={formatDateOnly(parseIsoDate(driver.deactivatedAt))}
          />
        </div>
        {isLocked ? (
          <Typography variant="body2" css={tw`text-shades-500`}>
            {LOCKED_NOTE}
          </Typography>
        ) : null}
      </SectionCard>
      <ConfirmActionModal
        isOpen={isConfirmOpen}
        title={isOnboarded ? 'Mark as not onboarded?' : 'Mark as onboarded?'}
        description={
          isOnboarded
            ? `${driverName} will be marked as not onboarded.`
            : `${driverName} will be marked as onboarded.`
        }
        confirmLabel={isOnboarded ? 'Mark not onboarded' : 'Mark onboarded'}
        isSubmitting={isSaving}
        onConfirm={handleConfirm}
        onClose={handleCloseConfirm}
        confirmDataTestId="partner-driver-onboarding-confirm-button"
      />
    </Fragment>
  )
}

export const OnboardingStatusCard = memo(OnboardingStatusCardComponent)
